import React, { useState, useRef, useEffect } from 'react';
import {
  Globe, Shield, CheckCircle2, Copy, ExternalLink,
  Upload, X, Info, RefreshCw, AlertCircle, Clock, Lock
} from 'lucide-react';
import userService from '../../../services/userServices';
import { useSiteContext } from '../../../context/SiteContext';

export default function WhiteLabel() {
  const { user } = useSiteContext();
  const fileInputRef = useRef(null);
  const iconInputRef = useRef(null);

  // Live whitelabel_enabled status from API (not cached localStorage)
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // White label configuration state
  const [config, setConfig] = useState({
    companyName: '',
    companyLogo: null,
    companyIcon: null,
    subdomainPrefix: '',
    domain: '',
    dnsVerified: false,
    dnsVerifiedAt: null,
    sslActive: false,
    sslExpiresAt: null,
    sslAutoRenewal: true,
  });

  const [saving, setSaving] = useState({
    branding: false,
    domain: false,
    dns: false,
    ssl: false,
  });

  const [verifyingDns, setVerifyingDns] = useState(false);
  const [generatingSsl, setGeneratingSsl] = useState(false);
  const [copied, setCopied] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch live whitelabel_enabled status from profile API
  useEffect(() => {
    const checkWhitelabelAccess = async () => {
      try {
        const profileRes = await userService.getUserProfile();
        if (profileRes.success && profileRes.user) {
          setWhitelabelEnabled(!!profileRes.user.whitelabel_enabled);
        } else {
          setWhitelabelEnabled(false);
        }
      } catch (err) {
        console.error('Failed to check whitelabel access:', err);
        setWhitelabelEnabled(false);
      }
    };
    checkWhitelabelAccess();
  }, []);

  // Load whitelabel settings once we know access is enabled
  useEffect(() => {
    if (!whitelabelEnabled) return;
    const loadWhiteLabelSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await userService.getWhiteLabelSettings();
        if (response.success && response.data) {
          const data = response.data;
          setConfig({
            companyName: data.companyName || '',
            companyLogo: data.companyLogo || null,
            companyIcon: data.companyIcon || null,
            subdomainPrefix: data.subdomainPrefix || '',
            domain: data.domain || '',
            dnsVerified: data.dnsVerified || false,
            dnsVerifiedAt: data.dnsVerifiedAt || null,
            sslActive: data.sslActive || false,
            sslExpiresAt: data.sslExpiresAt || null,
            sslAutoRenewal: data.sslAutoRenewal !== false,
          });
        }
      } catch (err) {
        console.error('Failed to load whitelabel settings:', err);
        setError('Failed to load whitelabel settings');
      } finally {
        setLoading(false);
      }
    };

    loadWhiteLabelSettings();
  }, [whitelabelEnabled]);

  // Show loading while checking access
  if (whitelabelEnabled === null) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-[#6a1fbf] animate-spin" />
          <p className="text-gray-400">Checking access...</p>
        </div>
      </div>
    );
  }

  if (!whitelabelEnabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-[#6a1fbf]/20 flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-[#6a1fbf]" />
          </div>
          <h2 className="text-xl font-bold text-white">White Label Not Enabled</h2>
          <p className="text-gray-400">
            White label features are not enabled for your account. Please contact your administrator to enable white label access.
          </p>
          <a
            href="mailto:support@r-link.com"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  // Show success message temporarily
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Generate the white label URL
  const whitelabelUrl = config.subdomainPrefix && config.domain
    ? `https://${config.subdomainPrefix}.${config.domain}`
    : null;

  // DNS record configuration
  const dnsRecord = {
    type: 'A',
    name: config.subdomainPrefix || 'your-subdomain',
    ipv4: '64.23.235.166',
    ttl: '3600',
  };

  const handleCopyUrl = () => {
    if (whitelabelUrl) {
      navigator.clipboard.writeText(whitelabelUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (event) => {
        setConfig(prev => ({ ...prev, companyLogo: event.target.result }));
      };
      reader.readAsDataURL(file);

      // Upload to server
      try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await userService.uploadWhiteLabelLogo(formData);
        if (response.success && response.data?.logoUrl) {
          setConfig(prev => ({ ...prev, companyLogo: response.data.logoUrl }));
          showSuccess('Logo uploaded successfully');
        }
      } catch (err) {
        console.error('Failed to upload logo:', err);
        setError('Failed to upload logo');
      }
    }
  };

  const handleRemoveLogo = () => {
    setConfig(prev => ({ ...prev, companyLogo: null }));
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      setError('Icon must be under 500KB');
      return;
    }

    // Validate square dimensions
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(img.src);
      if (Math.abs(img.width - img.height) > 2) {
        setError('Icon must be square (equal width and height)');
        return;
      }

      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (event) => {
        setConfig(prev => ({ ...prev, companyIcon: event.target.result }));
      };
      reader.readAsDataURL(file);

      // Upload to server
      try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await userService.uploadWhiteLabelIcon(formData);
        if (response.success && response.data?.iconUrl) {
          setConfig(prev => ({ ...prev, companyIcon: response.data.iconUrl }));
          showSuccess('Icon uploaded successfully');
        }
      } catch (err) {
        console.error('Failed to upload icon:', err);
        setError('Failed to upload icon');
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleRemoveIcon = () => {
    setConfig(prev => ({ ...prev, companyIcon: null }));
  };

  const handleSaveBranding = async () => {
    setSaving(prev => ({ ...prev, branding: true }));
    setError(null);
    try {
      const response = await userService.updateWhiteLabelBranding({
        companyName: config.companyName,
        companyLogo: config.companyLogo,
        companyIcon: config.companyIcon,
      });
      if (response.success) {
        showSuccess('Branding settings saved successfully');
      } else {
        setError(response.message || 'Failed to save branding settings');
      }
    } catch (err) {
      console.error('Failed to save branding:', err);
      setError(err.message || 'Failed to save branding settings');
    } finally {
      setSaving(prev => ({ ...prev, branding: false }));
    }
  };

  const handleSaveDomain = async () => {
    setSaving(prev => ({ ...prev, domain: true }));
    setError(null);
    try {
      const response = await userService.updateWhiteLabelDomain({
        subdomainPrefix: config.subdomainPrefix,
        domain: config.domain,
      });
      if (response.success) {
        // Update local state with response data (DNS/SSL may have been reset)
        if (response.data) {
          setConfig(prev => ({
            ...prev,
            dnsVerified: response.data.dnsVerified || false,
            dnsVerifiedAt: response.data.dnsVerifiedAt || null,
            sslActive: response.data.sslActive || false,
            sslExpiresAt: response.data.sslExpiresAt || null,
          }));
        }
        showSuccess('Domain settings saved successfully');
      } else {
        setError(response.message || 'Failed to save domain settings');
      }
    } catch (err) {
      console.error('Failed to save domain:', err);
      setError(err.message || 'Failed to save domain settings');
    } finally {
      setSaving(prev => ({ ...prev, domain: false }));
    }
  };

  const handleVerifyDns = async () => {
    setVerifyingDns(true);
    setError(null);
    try {
      const response = await userService.verifyWhiteLabelDns();
      if (response.success && response.data) {
        setConfig(prev => ({
          ...prev,
          dnsVerified: response.data.dnsVerified || true,
          dnsVerifiedAt: response.data.dnsVerifiedAt || new Date().toISOString(),
        }));
        showSuccess('DNS verified successfully');
      } else {
        setError(response.message || 'DNS verification failed');
      }
    } catch (err) {
      console.error('DNS verification failed:', err);
      setError(err.message || 'DNS verification failed');
    } finally {
      setVerifyingDns(false);
    }
  };

  const handleGenerateSsl = async () => {
    setGeneratingSsl(true);
    setError(null);
    try {
      const response = await userService.generateWhiteLabelSsl();
      if (response.success && response.data) {
        setConfig(prev => ({
          ...prev,
          sslActive: response.data.sslActive || true,
          sslExpiresAt: response.data.sslExpiresAt,
          sslAutoRenewal: response.data.sslAutoRenewal !== false,
        }));
        showSuccess('SSL certificate generated successfully');
      } else {
        setError(response.message || 'SSL certificate generation failed');
      }
    } catch (err) {
      console.error('SSL generation failed:', err);
      setError(err.message || 'SSL certificate generation failed');
    } finally {
      setGeneratingSsl(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return 0;
    const expiry = new Date(dateStr);
    const now = new Date();
    const diff = expiry - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const dnsProviders = [
    { name: 'GoDaddy', url: 'https://www.godaddy.com/help/add-an-a-record-19238' },
    { name: 'Namecheap', url: 'https://www.namecheap.com/support/knowledgebase/article.aspx/319/2237/how-can-i-set-up-an-a-address-record-for-my-domain/' },
    { name: 'HostGator', url: 'https://www.hostgator.com/help/article/changing-dns-records' },
    { name: 'Cloudflare', url: 'https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/' },
  ];

  const Tooltip = ({ children, content }) => (
    <div className="group relative inline-block">
      {children}
      <div className="invisible group-hover:visible absolute z-50 w-48 p-2 mt-1 text-xs text-gray-300 bg-white/10 backdrop-blur-xl border border-white/10 rounded-lg shadow-lg -left-20">
        {content}
      </div>
    </div>
  );

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-[#6a1fbf] animate-spin" />
          <p className="text-gray-400">Loading whitelabel settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-3 md:p-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Whitelabel Setup</h2>
        <p className="text-gray-400">Configure your custom domain with your branding</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-[#00c853]/10 border border-[#00c853]/30 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#00c853] flex-shrink-0" />
          <p className="text-[#00c853] font-medium">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Status Banner */}
      {whitelabelUrl && config.dnsVerified && config.sslActive && (
        <div className="bg-white/5 border border-[#00c853]/30 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#00c853]/20 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-[#00c853]" />
            </div>
            <div className="flex-1">
              <h3 className="text-[#00c853] font-semibold text-lg mb-1">Your Whitelabel URL is Live!</h3>
              <p className="text-gray-400 text-sm mb-3">Share this secure URL with your clients:</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1 bg-white/5 rounded-lg px-4 py-3 border border-white/10">
                  <a
                    href={whitelabelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00c853] hover:underline font-medium break-all"
                  >
                    {whitelabelUrl}
                  </a>
                </div>
                <button
                  onClick={handleCopyUrl}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 text-white px-4 py-3 rounded-lg font-bold transition-opacity"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section 1: Company Branding */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#6a1fbf]" />
            Configure Company Branding
          </h3>
          <Tooltip content="Your branding will appear on all white-labeled pages">
            <Info className="w-5 h-5 text-gray-500 cursor-help" />
          </Tooltip>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                Company Name
                <Tooltip content="Displayed in the browser tab and headers">
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                </Tooltip>
              </label>
              <input
                type="text"
                value={config.companyName}
                onChange={(e) => setConfig(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="Your Company Name"
                className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
              />
            </div>

            {/* Company Logo */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                Company Logo
                <Tooltip content="Recommended: PNG or SVG, max 2MB">
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                </Tooltip>
              </label>
              <div className="flex items-center gap-4">
                {config.companyLogo ? (
                  <div className="relative w-full h-24 bg-white/5 border border-dashed border-white/20 rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src={config.companyLogo}
                      alt="Company Logo"
                      className="max-h-20 max-w-full object-contain"
                    />
                    <button
                      onClick={handleRemoveLogo}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-24 bg-white/5 border border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-[#6a1fbf] hover:text-[#6a1fbf] transition-colors"
                  >
                    <Upload className="w-6 h-6" />
                    <span className="text-sm">Upload Logo</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Company Icon (Favicon) */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              Company Icon (Favicon)
              <Tooltip content="Square image, under 500KB. Used as the browser tab icon for your whitelabel domain.">
                <Info className="w-4 h-4 text-gray-500 cursor-help" />
              </Tooltip>
            </label>
            <div className="flex items-center gap-4">
              {config.companyIcon ? (
                <div className="relative w-24 h-24 bg-white/5 border border-dashed border-white/20 rounded-lg flex items-center justify-center overflow-hidden">
                  <img
                    src={config.companyIcon}
                    alt="Company Icon"
                    className="w-16 h-16 object-contain"
                  />
                  <button
                    onClick={handleRemoveIcon}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => iconInputRef.current?.click()}
                  className="w-24 h-24 bg-white/5 border border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-[#6a1fbf] hover:text-[#6a1fbf] transition-colors"
                >
                  <Upload className="w-5 h-5" />
                  <span className="text-xs">Upload Icon</span>
                </button>
              )}
              <div className="text-xs text-gray-500">
                <p>Square image (e.g. 64×64, 128×128)</p>
                <p>PNG or ICO recommended</p>
                <p>Max 500KB</p>
              </div>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/png,image/x-icon,image/svg+xml,image/jpeg"
                onChange={handleIconUpload}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveBranding}
              disabled={saving.branding}
              className="flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
            >
              {saving.branding ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Branding'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Section 2: Domain Configuration */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#6a1fbf]" />
            Configure Your Domain
          </h3>
          <Tooltip content="Use a subdomain like 'studio' or 'meetings'">
            <Info className="w-5 h-5 text-gray-500 cursor-help" />
          </Tooltip>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Subdomain Prefix */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                Subdomain Prefix <span className="text-red-400">*</span>
                <Tooltip content="The prefix before your domain (e.g., 'studio')">
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                </Tooltip>
              </label>
              <input
                type="text"
                value={config.subdomainPrefix}
                onChange={(e) => setConfig(prev => ({ ...prev, subdomainPrefix: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                placeholder="studio"
                className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
              />
            </div>

            {/* Domain */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                Your Domain <span className="text-red-400">*</span>
                <Tooltip content="Your registered domain name">
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                </Tooltip>
              </label>
              <input
                type="text"
                value={config.domain}
                onChange={(e) => setConfig(prev => ({ ...prev, domain: e.target.value.toLowerCase() }))}
                placeholder="yourdomain.com"
                className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors"
              />
            </div>
          </div>

          {/* URL Preview */}
          {whitelabelUrl && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-gray-500 text-sm mb-1">Your whitelabel URL will be:</p>
              <p className="text-[#00c853] font-medium">{whitelabelUrl}</p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleSaveDomain}
              disabled={saving.domain || !config.subdomainPrefix || !config.domain}
              className="flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
            >
              {saving.domain ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Domain Settings'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Section 3: DNS Configuration */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#6a1fbf]" />
            Set Up DNS A Record
          </h3>
          <Tooltip content="DNS changes may take up to 48 hours to propagate">
            <Info className="w-5 h-5 text-gray-500 cursor-help" />
          </Tooltip>
        </div>

        <div className="space-y-6">
          {/* DNS Record Details */}
          <div className="bg-white/5 border border-[#6a1fbf]/30 rounded-xl p-5">
            <p className="text-[#6a1fbf] font-medium mb-4">Add This A Record to Your Domain Provider:</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Type:</p>
                  <p className="text-white font-mono font-bold">{dnsRecord.type}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Name/Host:</p>
                  <p className="text-white font-mono font-bold">{dnsRecord.name}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">IPv4 Address:</p>
                  <p className="text-[#00c853] font-mono font-bold">{dnsRecord.ipv4}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">TTL:</p>
                  <p className="text-white font-mono font-bold">{dnsRecord.ttl} (or default)</p>
                </div>
              </div>
            </div>
            <p className="text-gray-400 text-sm mt-4">
              <strong className="text-white">Result:</strong> When someone visits {whitelabelUrl || `${config.subdomainPrefix || 'your-subdomain'}.${config.domain || 'yourdomain.com'}`}, they'll be directed to R-Link's servers with your branding.
            </p>
          </div>

          {/* Provider Quick Links */}
          <div>
            <p className="text-gray-400 text-sm mb-3">Need help? Click your provider below for instructions:</p>
            <div className="flex flex-wrap gap-2">
              {dnsProviders.map((provider) => (
                <a
                  key={provider.name}
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                >
                  {provider.name}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>

          {/* Verification Status */}
          {config.dnsVerified ? (
            <div className="bg-[#00c853]/10 border border-[#00c853]/30 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#00c853] flex-shrink-0" />
              <div>
                <p className="text-[#00c853] font-medium">DNS Verified Successfully!</p>
                <p className="text-gray-400 text-sm">Verified on {formatDate(config.dnsVerifiedAt)}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <button
                onClick={handleVerifyDns}
                disabled={verifyingDns || !config.subdomainPrefix || !config.domain}
                className="flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
              >
                {verifyingDns ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Verify DNS
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section 4: SSL Certificate */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#6a1fbf]" />
            Activate SSL Certificate
          </h3>
          <Tooltip content="SSL certificates are required for secure connections">
            <Info className="w-5 h-5 text-gray-500 cursor-help" />
          </Tooltip>
        </div>

        <div className="space-y-6">
          {config.sslActive ? (
            <>
              {/* SSL Active Status */}
              <div className="bg-[#00c853]/10 border border-[#00c853]/30 rounded-xl p-5">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#00c853]/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-[#00c853]" />
                    </div>
                    <div>
                      <p className="text-[#00c853] font-semibold">SSL Certificate Active</p>
                      <p className="text-gray-400 text-sm">Your domain is secured with HTTPS encryption</p>
                    </div>
                  </div>
                  <span className="bg-[#00c853] text-white text-xs font-medium px-3 py-1 rounded-full">Live</span>
                </div>
              </div>

              {/* SSL Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Expires On
                  </div>
                  <p className="text-white text-lg font-semibold">{formatDate(config.sslExpiresAt)}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Days Remaining
                  </div>
                  <p className="text-white text-lg font-semibold">{getDaysRemaining(config.sslExpiresAt)} days</p>
                </div>
              </div>

              {/* Auto Renewal */}
              <div className="bg-[#6a1fbf]/10 border border-[#6a1fbf]/30 rounded-xl p-4 flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-[#6a1fbf] flex-shrink-0" />
                <div>
                  <p className="text-[#6a1fbf] font-medium">Auto-Renewal Enabled</p>
                  <p className="text-gray-400 text-sm">Certificate renews automatically before expiration</p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* SSL Not Active */}
              <div className="bg-[#ffa500]/10 border border-[#ffa500]/30 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#ffa500] flex-shrink-0" />
                  <div>
                    <p className="text-[#ffa500] font-medium">SSL Certificate Required</p>
                    <p className="text-gray-400 text-sm">Generate an SSL certificate to enable secure HTTPS access for your white-label domain.</p>
                  </div>
                </div>
              </div>

              {!config.dnsVerified && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                  <Info className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <p className="text-gray-400 text-sm">
                    DNS verification is required before generating an SSL certificate.
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleGenerateSsl}
                  disabled={generatingSsl || !config.dnsVerified}
                  className="flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
                >
                  {generatingSsl ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Generating Certificate...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Generate SSL Certificate
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Save All Changes */}
      <div className="flex justify-end">
        <button
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity font-bold"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
